import React, { useRef, useEffect, useState, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import { Logger } from "../../tools/Logger";
import axios from "axios";
import {
  isPrivacyVideoEnabled,
  objectFitCoverBox,
  PRIVACY_BLUR_PX,
  PrivacyProcessor,
} from "./privacy";

export const VideoQuic = ({
  ipAddresses,
  uavId,
  privacyEnabled,
  blurEnabled,
  personDetectionEnabled,
  showDebug,
  instanceKey,
}: {
  ipAddresses: Array<string>;
  uavId: string;
  /**
   * Legacy combined switch. When blur/detect props are omitted, this (or the
   * env fallback) enables both. Prefer the split props.
   */
  privacyEnabled?: boolean;
  /** ROI blur when a cyan ring is present. Default follows privacyEnabled. */
  blurEnabled?: boolean;
  /** Stephan-best person boxes. Default follows privacyEnabled. */
  personDetectionEnabled?: boolean;
  /** Show SIM / stall debug chrome (useful on /video-test). */
  showDebug?: boolean;
  /**
   * Key for the /video-test chaos hook registry. Defaults to uavId. Only set
   * this where the same aircraft is mounted more than once, so the mounts do
   * not overwrite each other's hooks.
   */
  instanceKey?: string;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const privacyCanvasRef = useRef<HTMLCanvasElement>(null);
  const privacyProcessorRef = useRef<PrivacyProcessor | null>(null);
  const transportRefs = [
    useRef<WebTransport | null>(null),
    useRef<WebTransport | null>(null),
  ];
  const datagramReaders = [
    useRef<ReadableStreamDefaultReader | null>(null),
    useRef<ReadableStreamDefaultReader | null>(null),
  ];
  const fragmentBuffers: Array<{ [key: string]: any }> = [{}, {}];
  const latestServerTimestampRef = useRef(0);
  const [showVideoDebugTag] = useState<boolean>(!!showDebug);
  const [currentStream, setCurrentStream] = useState<string>("");
  const [isStalled, setIsStalled] = useState<boolean>(false);
  const [hasFrame, setHasFrame] = useState<boolean>(false);
  const [connectStatus, setConnectStatus] = useState<string>("Connecting…");
  const [privacyPeek, setPrivacyPeek] = useState<boolean>(false);
  const [privacyActive, setPrivacyActive] = useState<boolean>(false);
  /** True while the worker is painting ROI blur. */
  const [privacyBlurActive, setPrivacyBlurActive] = useState<boolean>(false);
  /** True after a cyan ROI was confirmed at least once — gates CSS fallback. */
  const [privacyRoiConfirmed, setPrivacyRoiConfirmed] = useState<boolean>(false);
  const [privacyLayout, setPrivacyLayout] = useState({
    left: 0,
    top: 0,
    width: "100%" as number | string,
    height: "100%" as number | string,
  });
  const TAG = "QUICDASH";
  const lastResponseTimes = [useRef<number>(0), useRef<number>(0)];
  const lastFrameTime = [useRef<number>(Date.now()), useRef<number>(Date.now())];
  const isConnecting = [useRef<boolean>(false), useRef<boolean>(false)];
  const pendingPromises = useRef<Promise<any>[]>([]);
  const lastCloseTime = [useRef<number>(0), useRef<number>(0)];
  const connectAbortControllers = [useRef<AbortController | null>(null), useRef<AbortController | null>(null)];
  const lastFailureTime = [useRef<number>(0), useRef<number>(0)];

  const combinedDefault =
    privacyEnabled !== undefined ? privacyEnabled : isPrivacyVideoEnabled();
  const blurOn =
    blurEnabled !== undefined ? blurEnabled : combinedDefault;
  const detectOn =
    personDetectionEnabled !== undefined
      ? personDetectionEnabled
      : combinedDefault;
  /** Processor mounted when either feature is on. */
  const privacyOn = blurOn || detectOn;
  /**
   * CSS blur only after an ROI was confirmed and the overlay is currently
   * down (stall/error). Startup and no-ring feeds stay raw.
   */
  const cssBlurFallback =
    blurOn &&
    privacyRoiConfirmed &&
    !privacyBlurActive &&
    !privacyActive &&
    !privacyPeek;

  /** Align the overlay canvas to the object-fit: cover box of a given frame size. */
  const applyPrivacyLayout = useCallback(
    (mediaWidth: number, mediaHeight: number) => {
      const container = containerRef.current;
      if (!container || mediaWidth <= 0 || mediaHeight <= 0) {
        return;
      }
      setPrivacyLayout(
        objectFitCoverBox(
          container.clientWidth,
          container.clientHeight,
          mediaWidth,
          mediaHeight
        )
      );
    },
    []
  );

  /**
   * Re-measure using whatever frame size we know. The worker reports its own
   * source size, so this is only a fallback for before the first processed
   * frame — img load events are unreliable here because each new frame
   * replaces img.src and cancels the pending load.
   */
  const updatePrivacyLayout = useCallback(() => {
    const img = imgRef.current;
    const container = containerRef.current;
    if (img?.naturalWidth && img.naturalHeight) {
      applyPrivacyLayout(img.naturalWidth, img.naturalHeight);
    }
    // The worker blurs in source pixels but the radius is defined in display
    // pixels, so it needs the container size to convert.
    if (privacyProcessorRef.current && container) {
      privacyProcessorRef.current.setContainerSize(
        container.clientWidth,
        container.clientHeight
      );
    }
  }, [applyPrivacyLayout]);

  (window as any).__QUIC_IDLE_TIMEOUT = 30000;  // 30 seconds
  (window as any).__QUIC_MAX_STREAMS = 100; // arbitrary limit for concurrent streams

  const retryDelay = 60000; // 60s, this stop the quic constant retry causing backoff buildup
  const busyLoopDelay = 100; // arbitary 100ms delay to prevent busy loop
  const connectTimeout = 10000; // 10s timeout for connection attempts
  const heartbeatInterval = connectTimeout/5; // 5s interval had some dropped packets, so we use 2s
  const staleFrame = 5000;

  function isValidJPEG(data: Uint8Array) {
    const isStartOfImage = data[0] === 0xff && data[1] === 0xd8;
    const isEndOfImage = data[data.length - 2] === 0xff && data[data.length - 1] === 0xd9;
    return isStartOfImage && isEndOfImage;
  }

  function handleFragment(
    buffer: any,
    frameId: number,
    totalFragments: number,
    fragmentIndex: number,
    fragmentData: Uint8Array
  ) {
    if (!buffer[frameId]) {
      buffer[frameId] = {
        fragments: Array(totalFragments).fill(null),
        receivedCount: 0,
      };
    }
    const frameBuffer = buffer[frameId];
    if (!frameBuffer.fragments[fragmentIndex]) {
      frameBuffer.fragments[fragmentIndex] = fragmentData;
      frameBuffer.receivedCount += 1;
    }
    if (frameBuffer.receivedCount === totalFragments) {
      const fullFrame = new Uint8Array(
        frameBuffer.fragments.reduce(
          (acc: number, frag: Uint8Array) => acc + frag.length,
          0
        )
      );
      let offset = 0;
      for (const frag of frameBuffer.fragments) {
        fullFrame.set(frag, offset);
        offset += frag.length;
      }
      delete buffer[frameId];
      return fullFrame;
    }
    return null;
  }

  const trackPromise = <T,>(promise: Promise<T>): Promise<T> => {
    pendingPromises.current.push(promise);
    return promise.finally(() => {
      const index = pendingPromises.current.indexOf(promise);
      if (index !== -1) {
        pendingPromises.current.splice(index, 1);
      }
    });
  };

  const waitForPendingPromises = async (): Promise<void> => {
    if (pendingPromises.current.length > 0) {
      Logger.info(TAG, `Waiting for ${pendingPromises.current.length} pending operations to complete...`);
      try {
        await Promise.all(pendingPromises.current);
      } catch (error) {
        Logger.error(TAG, `Error waiting for pending promises: ${error}`);
      }
      Logger.info(TAG, `All pending operations completed`);
    }
  };

  const forceCloseTransport = async (index: number): Promise<void> => {
    const transport = transportRefs[index].current;
    if (!transport) return;
    
    Logger.info(TAG, `Force closing all resources for SIM ${index + 1}...`);
    
    try {
      const reader = datagramReaders[index].current;
      if (reader) {
        await reader.cancel("Force close").catch(() => {});
        datagramReaders[index].current = null;
        Logger.info(TAG, `Reader canceled for SIM ${index + 1}`);
      }
    } catch (e) {
      Logger.error(TAG, `Error canceling reader: ${e}`);
    }
    
    try {
      transport.close();
      Logger.info(TAG, `Transport closed for SIM ${index + 1}`);
    } catch (e) {
      Logger.error(TAG, `Error closing transport: ${e}`);
    }
    
    transportRefs[index].current = null;
    
    Object.keys(fragmentBuffers[index]).forEach(key => {
      delete fragmentBuffers[index][key];
    });
    fragmentBuffers[index] = {};
    
    Logger.info(TAG, `All resources closed for SIM ${index + 1}`);
    lastCloseTime[index].current = Date.now();
  };

  const axiosInstance = axios.create({
    timeout: 5000,
  });

  const checkHealth = async (index: number, abortSignal: AbortSignal): Promise<boolean> => {
    const url = `https://${ipAddresses[index]}:5002/health`;
    try {
      Logger.info(TAG, `Checking health for SIM ${index + 1} at ${ipAddresses[index]} on ${uavId}`);
      const response = await axiosInstance.get(url);
      if (response.status === 200) {
        Logger.info(TAG, `Health check passed for SIM ${index + 1} at ${ipAddresses[index]} on ${uavId}`);
        return true;
      } else {
        Logger.warn(TAG, `Health check failed for SIM ${index + 1} on ${uavId}.`);
        return false;
      }
    } catch (error) {
      if (abortSignal.aborted) return false;
      
      if (axios.isAxiosError(error) && error.code === "ECONNABORTED") {
        Logger.warn(TAG, `Health check timed out for SIM ${index + 1} on ${uavId}.`);
      } else {
        Logger.error(TAG, `Health check error for SIM ${index + 1} on ${uavId}: ${error}`);
      }
      return false;
    }
  };

  let isCleaningUp = false;

  const cleanup = async () => {
    isCleaningUp = true;
    Logger.info(TAG, `[PERF] Starting cleanup at ${Date.now()} for ${uavId}`);
    await waitForPendingPromises();
    Logger.info(TAG, `[PERF] Pending promises cleared at ${Date.now()} for ${uavId}. Cleaning up transports`);
    await Promise.all([
      forceCloseTransport(0),
      forceCloseTransport(1)
    ]);
    Logger.info(TAG, `[PERF] forceCloseTransport finished at ${Date.now()} for ${uavId}, cleanup complete`);
    isCleaningUp = false;
  };

  const startStream = async (index: number, abortSignal: AbortSignal) => {
    connectAbortControllers[index].current = new AbortController();
    const connectAbortSignal = connectAbortControllers[index].current!.signal;
    const streamPromise = (async () => {
      Logger.info(TAG, `[PERF] startStream(${index}) called at ${Date.now()} for ${uavId}`);

      while (isCleaningUp) {
        Logger.info(TAG, `[PERF] Waiting for cleanup before starting stream ${index} at ${Date.now()} for ${uavId}`);
        await new Promise(resolve => setTimeout(resolve, busyLoopDelay));
      }
      if (isConnecting[index].current) {
        Logger.info(TAG, `[PERF] Already connecting for SIM ${index + 1} at ${Date.now()} for ${uavId}`);
        return;
      }
      isConnecting[index].current = true;
      Logger.info(TAG, `[PERF] Forcing close of previous transport for SIM ${index + 1} at ${Date.now()} for ${uavId}`);
      await forceCloseTransport(index);
      await new Promise(resolve => setTimeout(resolve, busyLoopDelay));
      if (abortSignal.aborted) {
        Logger.info(TAG, `[PERF] Aborted before connection for SIM ${index + 1} at ${Date.now()} for ${uavId}`);
        isConnecting[index].current = false;
        return;
      }
      const fragmentBuffer = fragmentBuffers[index];
      let temporaryTransport: WebTransport | null = null;

      try {
        Logger.info(TAG, `[PERF] Health check starting for SIM ${index + 1} at ${Date.now()} for ${uavId}`);
        const healthStart = Date.now();
        const isHealthy = await checkHealth(index, abortSignal);
        Logger.info(TAG, `[PERF] Health check finished for SIM ${index + 1} at ${Date.now()} (duration: ${Date.now() - healthStart}ms) for ${uavId}`);
        if (abortSignal.aborted) {
          Logger.info(TAG, `[PERF] Aborted after health check for SIM ${index + 1} at ${Date.now()} for ${uavId}`);
          isConnecting[index].current = false;
          return;
        }
        if (!isHealthy) {
          Logger.warn(TAG, `[PERF] Health check failed for SIM ${index + 1} at ${Date.now()} for ${uavId}`);
          setConnectStatus(`Health failed: ${ipAddresses[index] || `SIM ${index + 1}`}`);
          throw new Error(`Health check failed for SIM ${index + 1}`);
        }

        Logger.info(TAG, `[PERF] Starting WebTransport connection for SIM ${index + 1} at ${Date.now()} for ${uavId}`);
        const connectStart = Date.now();
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error(`Connection timeout for SIM ${index + 1}`)), connectTimeout);
        });

        const url = `https://${ipAddresses[index]}:5001/wt?sim=${index + 1}&uavId=${uavId}`;
        Logger.info(TAG, `[PERF] WebTransport URL for SIM ${index + 1}: ${url}`);

        const transport = await Promise.race([
          (async () => {
            const startTime = performance.now();
            temporaryTransport = new WebTransport(
              url,
              {
                congestionControl: 'cubic',
                QUIC_IDLE_TIMEOUT_MILLISECONDS: 30000,
                requireUnreliable: true,
                allowPooling: false,
                serverTimeout: connectTimeout
              } as any
            );
            
            temporaryTransport.closed
              .then(() => Logger.info(TAG, `WebTransport closed for SIM ${index + 1} on ${uavId}`))
              .catch(error => Logger.error(TAG, `WebTransport closed with error for SIM ${index + 1} on ${uavId}: ${error}`));
            
            Logger.info(TAG, `[PERF] Waiting for WebTransport ready state for SIM ${index + 1} at ${Date.now()} for ${uavId}`);
            await Promise.race([
              temporaryTransport.ready,
              new Promise((_, reject) => {
                connectAbortSignal.addEventListener("abort", () => reject(new Error("Connection aborted by other SIM")), { once: true });
              })
            ]);
            const endTime = performance.now();

            if (abortSignal.aborted) {
              Logger.info(TAG, `[PERF] Aborted during WebTransport ready for SIM ${index + 1} at ${Date.now()} for ${uavId}`);
              temporaryTransport.close();
              return null;
            }

            Logger.info(
              TAG,
              `[PERF] WebTransport connection established in ${endTime - startTime}ms for SIM ${index + 1} at ${Date.now()} for ${uavId}`
            );

            return temporaryTransport;
          })(),
          timeoutPromise
        ]).catch(error => {
          if (temporaryTransport) {
            temporaryTransport.close();
            temporaryTransport = null;
          }
          Logger.error(TAG, `[PERF] WebTransport connection failed for SIM ${index + 1} at ${Date.now()} for ${uavId}: ${error}`);
          throw error;
        });

        const otherIndex = index === 0 ? 1 : 0;
        if (connectAbortControllers[otherIndex].current) {
          connectAbortControllers[otherIndex].current!.abort();
        }

        Logger.info(TAG, `[PERF] WebTransport connection finished for SIM ${index + 1} at ${Date.now()} (duration: ${Date.now() - connectStart}ms) for ${uavId}`);

        if (!transport) {
          throw new Error(`Transport initialization failed for SIM ${index + 1} on ${uavId}.`);
        }

        transportRefs[index].current = transport;
        lastFrameTime[index].current = Date.now();

        try {
          const heartbeat = {
            type: 'heartbeat',
            timestamp: Date.now(),
            sim: index + 1
          };
          const writer = transport.datagrams.writable.getWriter();
          await writer.write(new TextEncoder().encode(JSON.stringify(heartbeat)));
          writer.releaseLock();
          lastResponseTimes[index].current = Date.now();
          Logger.info(TAG, `Sent heartbeat for SIM ${index + 1} on ${uavId}`);
        } catch (error) {
          Logger.error(TAG, `Failed to send initial heartbeat for SIM ${index + 1}: ${error}`);
        }
        
        const datagramReader = transport.datagrams.readable.getReader();
        datagramReaders[index].current = datagramReader;
        
        while (!abortSignal.aborted) {
          const currentTime = Date.now();
      
          if (currentTime - lastFrameTime[index].current > connectTimeout) {
            Logger.warn(TAG, `No frames received for 10s on SIM ${index + 1} ${uavId}, closing connection`);
            throw new Error(`Frame timeout for SIM ${index + 1} on ${uavId}`);
          }

          if (currentTime - lastResponseTimes[index].current >= heartbeatInterval) {
            try {
              const heartbeat = {
                type: 'heartbeat',
                timestamp: currentTime,
                sim: index + 1
              };
              const writer = transport.datagrams.writable.getWriter();
              await writer.write(new TextEncoder().encode(JSON.stringify(heartbeat)));
              writer.releaseLock();
              lastResponseTimes[index].current = currentTime;
              Logger.info(TAG, `Sent heartbeat for SIM ${index + 1} on ${uavId}`);
            } catch (error) {
              Logger.error(TAG, `Failed to send heartbeat for SIM ${index + 1}: ${error}`);
              throw error;
            }
          }

          const { value, done } = await datagramReader.read();
          if (done || !value) {
            throw new Error(`Stream ${index + 1} disconnected on ${uavId}.`);
          }
          
          const dataView = new DataView(value.buffer);
          const frameId = dataView.getUint16(0, false);
          const totalFragments = dataView.getUint8(2);
          const fragmentIndex = dataView.getUint8(3);
          const fragmentLength = dataView.getUint16(4, false);
          const serverTimestamp = Number(dataView.getBigUint64(6, false));
          
          if (
            serverTimestamp < latestServerTimestampRef.current &&
            latestServerTimestampRef.current !== 0
          ) {
            continue;
          }
          
          latestServerTimestampRef.current = serverTimestamp;
          
          const fragmentData = new Uint8Array(value.buffer, 14, fragmentLength);
          const fullFrame = handleFragment(
            fragmentBuffer,
            frameId,
            totalFragments,
            fragmentIndex,
            fragmentData
          );
          
          if (!imgRef.current) {
            continue;
          }
          
          if (fullFrame && !isValidJPEG(fullFrame)) {
            Logger.error(TAG, `Full frame is not valid JPEG on ${uavId}`);
            continue;
          }

          if (fullFrame) {
            const clientTimestamp = Date.now();
            lastFrameTime[index].current = currentTime;
            const latency = clientTimestamp - serverTimestamp;
            const latencyThreshold = 3000; // arbitary
            if (latency > latencyThreshold) {
              Logger.error(TAG, `Latency ${latency} on ${uavId}`);
            }

            // Authoritative safety path: always paint raw JPEG first.
            const blob = new Blob([fullFrame], { type: "image/jpeg" });
            const objectURL = URL.createObjectURL(blob);
            imgRef.current.src = objectURL;
            setHasFrame(true);
            setConnectStatus(`Live · SIM ${index + 1}`);
            setCurrentStream(`SIM ${index + 1} (${ipAddresses[index]})`);

            // Best-effort privacy — never awaited; never gates the raw paint above.
            const privacy = privacyProcessorRef.current;
            if (privacy) {
              privacy.enqueueFrame(blob);
            }

            setTimeout(() => URL.revokeObjectURL(objectURL), busyLoopDelay);
          }
        }
      } catch (error) {
        if (!abortSignal.aborted) {
          await forceCloseTransport(index);
          Logger.error(
            TAG,
            `WebTransport connection failed for SIM ${index + 1} on ${uavId}: ${error}`
          );
          setConnectStatus(
            `WebTransport :5001 failed SIM ${index + 1} (${ipAddresses[index] || "?"}): ${
              error instanceof Error ? error.message : String(error)
            }. Health :5002 can be up while video service is down.`
          );
          isConnecting[index].current = false;

          lastFailureTime[index].current = Date.now();

          const otherIndex = index === 0 ? 1 : 0;
          if (
            !transportRefs[otherIndex].current &&
            !isConnecting[otherIndex].current
          ) {
            Logger.info(TAG, `Attempting to start SIM ${otherIndex + 1} after SIM ${index + 1} failure on ${uavId}`);
            startStream(otherIndex, abortSignal);
          }
          return;
        }
      } finally {
        if (abortSignal.aborted) {
          isConnecting[index].current = false;
        }
      }
    })();
    
    trackPromise(streamPromise);
  };

  useEffect(() => {
    const handleBeforeUnload = () => {
      Logger.info(TAG, `Page unloading, force closing all transports`);
      transportRefs.forEach((ref, index) => {
        if (ref.current) {
          try {
            ref.current.close();
          } catch (e) {
          }
        }
      });
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    const abortControllers = [new AbortController(), new AbortController()];
    Logger.info(
      TAG,
      `Video QUIC component starting for ${uavId} ${ipAddresses[0]} ${ipAddresses[1]}`
    );

    startStream(0, abortControllers[0].signal);
    startStream(1, abortControllers[1].signal);

    const interval = setInterval(async () => {
      for (let i = 0; i < ipAddresses.length; i++) {
        if (!isConnecting[i].current) {
          const healthy = await checkHealth(i, abortControllers[i].signal);
          if (healthy) {
            Logger.info(TAG, `Periodic health check passed for SIM ${i + 1} on ${uavId}`);
            return;
          }
          Logger.info(TAG, `Periodic health check failed for SIM ${i + 1} on ${uavId}`);
        }
      }
    }, retryDelay);

    return () => {
      Logger.info(TAG, `Cleaning up component for ${uavId}`);

      abortControllers.forEach(controller => {
        if (!controller.signal.aborted) {
          controller.abort();
        }
      });

      clearInterval(interval);

      waitForPendingPromises().then(() => cleanup());
    };
  }, []);

  useEffect(() => {
    const stallCheckInterval = setInterval(() => {
      const lastFrame = Math.max(lastFrameTime[0].current, lastFrameTime[1].current);
      setIsStalled(Date.now() - lastFrame > staleFrame);
    }, 1000);

    return () => clearInterval(stallCheckInterval);
  }, []);

  // Privacy overlay lifecycle — independent of transport. Instant unmount-safe.
  // blurOn / detectOn are applied via setters below so toggling one feature
  // cannot re-transferControlToOffscreen on a live canvas.
  useEffect(() => {
    if (!privacyOn) {
      setPrivacyPeek(false);
      setPrivacyActive(false);
      setPrivacyBlurActive(false);
      setPrivacyRoiConfirmed(false);
      return;
    }
    const canvas = privacyCanvasRef.current;
    if (!canvas) {
      return;
    }

    let processor: PrivacyProcessor;
    try {
      processor = new PrivacyProcessor(uavId, canvas, {
        blurEnabled: blurOn,
        personDetectionEnabled: detectOn,
      });
    } catch (error) {
      Logger.error(TAG, `Privacy processor init failed on ${uavId}: ${error}`);
      return;
    }

    // Fresh processor always starts with peek off — clear any leftover React state.
    // Startup is raw until a cyan ROI is confirmed (fail-open for no-ring feeds).
    setPrivacyPeek(false);
    setPrivacyActive(false);
    setPrivacyBlurActive(false);
    setPrivacyRoiConfirmed(false);

    processor.setOverlayChangeListener((visible) => {
      setPrivacyActive(visible);
    });
    processor.setBlurActiveListener((active) => {
      setPrivacyBlurActive(active);
    });
    processor.setRoiConfirmedListener((confirmed) => {
      setPrivacyRoiConfirmed(confirmed);
    });
    processor.setFrameSizeListener(applyPrivacyLayout);
    privacyProcessorRef.current = processor;

    // Report geometry immediately so ROI blur can convert display-pixel radius.
    const container = containerRef.current;
    if (container) {
      processor.setContainerSize(
        container.clientWidth,
        container.clientHeight
      );
    }
    Logger.info(
      TAG,
      `Privacy worker overlay enabled for ${uavId} (blur=${blurOn}, detect=${detectOn})`
    );

    // Chaos hooks for /video-test — never used by production paint path.
    const hookKey = instanceKey || uavId;
    (window as any).__mannaPrivacy = (window as any).__mannaPrivacy || {};
    (window as any).__mannaPrivacy[hookKey] = {
      forceFail: (reason?: string) =>
        processor.forceFailForChaos(reason || "chaos"),
      peek: () => {
        const peeking = processor.togglePeek();
        setPrivacyPeek(peeking);
        return peeking;
      },
      isCircuitOpen: () => processor.isCircuitOpen(),
    };

    return () => {
      processor.dispose();
      privacyProcessorRef.current = null;
      setPrivacyPeek(false);
      setPrivacyActive(false);
      setPrivacyBlurActive(false);
      setPrivacyRoiConfirmed(false);
      if ((window as any).__mannaPrivacy) {
        delete (window as any).__mannaPrivacy[hookKey];
      }
    };
    // Intentionally omit blurOn/detectOn — see settings sync effect below.
  }, [privacyOn, uavId, instanceKey, applyPrivacyLayout]);

  // Hot-swap blur / detect without remounting the OffscreenCanvas transfer.
  useEffect(() => {
    const processor = privacyProcessorRef.current;
    if (!privacyOn || !processor) {
      return;
    }
    processor.setBlurEnabled(blurOn);
    processor.setPersonDetectionEnabled(detectOn);
  }, [privacyOn, blurOn, detectOn]);

  // Keep privacy canvas aligned to object-fit: cover content box.
  useEffect(() => {
    if (!privacyOn) {
      return;
    }
    updatePrivacyLayout();
    const container = containerRef.current;
    // Avoid bare ResizeObserver — older @types in CI don't declare it.
    const ResizeObserverCtor = (window as any).ResizeObserver as
      | (new (cb: () => void) => { disconnect: () => void; observe: (el: Element) => void })
      | undefined;
    if (!container || !ResizeObserverCtor) {
      return;
    }
    const ro = new ResizeObserverCtor(() => updatePrivacyLayout());
    ro.observe(container);
    return () => ro.disconnect();
  }, [privacyOn, hasFrame, updatePrivacyLayout]);

  const onPrivacyClick = (event: React.MouseEvent) => {
    if (!privacyOn || !privacyProcessorRef.current) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const peeking = privacyProcessorRef.current.togglePeek();
    setPrivacyPeek(peeking);
    Logger.info(
      TAG,
      peeking
        ? `Privacy peek ON (raw) for ${uavId}`
        : `Privacy peek OFF (blur restored) for ${uavId}`
    );
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        width: "100%",
        height: 290,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        // Privacy canvas is sized to the object-fit: cover box, which overhangs
        // this container. Clip it so the overlay can't paint over card chrome.
        overflow: "hidden",
        backgroundColor: "#000",
      }}
    >
      <img
        ref={imgRef}
        alt="Video stream"
        onLoad={updatePrivacyLayout}
        onError={(e) => {
          Logger.error(TAG, `Image load error on ${uavId}: ${e.currentTarget.src}`);
        }}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
          // Startup and no-ring feeds stay raw. CSS blur is only a fallback
          // after a cyan ROI was confirmed and the overlay is currently down
          // (stall / worker error). Scale stays on whenever blur is active or
          // that fallback is showing, so the 2% zoom does not pop.
          filter: cssBlurFallback ? `blur(${PRIVACY_BLUR_PX}px)` : "none",
          transform:
            (privacyBlurActive || cssBlurFallback) && !privacyPeek
              ? "scale(1.02)"
              : "none",
        }}
      />
      {privacyOn ? (
        <canvas
          ref={privacyCanvasRef}
          title={
            privacyPeek
              ? "Click to re-enable privacy overlay"
              : privacyBlurActive
                ? "Click to peek raw feed"
                : "Click to hide person boxes / overlay"
          }
          onClick={onPrivacyClick}
          style={{
            position: "absolute",
            left: privacyLayout.left,
            top: privacyLayout.top,
            width: privacyLayout.width,
            height: privacyLayout.height,
            opacity: privacyActive && !privacyPeek ? 1 : 0,
            cursor: "pointer",
            pointerEvents: "auto",
          }}
        />
      ) : null}
      {!hasFrame ? (
        <Typography
          variant="caption"
          color="white"
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            padding: 2,
            backgroundColor: "rgba(0,0,0,0.55)",
            zIndex: 5,
            pointerEvents: "none",
            whiteSpace: "pre-line",
          }}
        >
          {`No video frames yet\n${connectStatus}\nHosts: ${(ipAddresses || []).filter(Boolean).join(" · ") || "none"}`}
        </Typography>
      ) : null}
      {privacyOn && privacyPeek ? (
        <Typography
          variant="caption"
          color="white"
          sx={{
            position: "absolute",
            top: 8,
            left: 8,
            backgroundColor: "rgba(0,0,0,0.65)",
            padding: "2px 8px",
            borderRadius: "4px",
            zIndex: 11,
            pointerEvents: "none",
          }}
        >
          RAW (peek)
        </Typography>
      ) : null}
      {isStalled && (showVideoDebugTag || hasFrame) && (
        <Box
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            width: 16,
            height: 16,
            borderRadius: "50%",
            backgroundColor: "#FFC107",
            border: "2px solid white",
            zIndex: 10,
          }}
        />
      )}
      {(showVideoDebugTag || hasFrame) && currentStream && (
          <Typography
          variant="caption"
          color="white"
          sx={{
            position: "absolute",
            bottom: 8,
            left: 8,
            backgroundColor: "rgba(0,0,0,0.6)",
            padding: "4px 8px",
            borderRadius: "4px",
            zIndex: 10,
          }}
        >
          {currentStream}
        </Typography>
      )}
    </Box>
  );
};
